import { useEffect, useState } from 'react';
import { Printer, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';

const API = process.env.REACT_APP_BACKEND_URL;
const headers = () => ({ Authorization: `Bearer ${localStorage.getItem('backoffice_token')}` });

export const money = (cents) => `$${((cents || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' }) : '-');

export const INVOICE_STATUS = {
  paid: { label: 'Paid', color: 'bg-green-100 text-green-700' },
  open: { label: 'Open', color: 'bg-blue-100 text-blue-700' },
  past_due: { label: 'Past due', color: 'bg-red-100 text-red-700' },
  failed: { label: 'Failed', color: 'bg-red-100 text-red-700' },
  void: { label: 'Void', color: 'bg-gray-100 text-gray-500' },
  voided: { label: 'Void', color: 'bg-gray-100 text-gray-500' },
};

/** One invoice, every line: the plan, then texts and AI requests over the plan. Printable. */
export function InvoiceDialog({ invoiceId, onClose, onPay, paying }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!invoiceId) return;
    setData(null);
    setError(null);
    fetch(`${API}/api/billing/v2/invoices/${invoiceId}`, { headers: headers() })
      .then(async (r) => {
        const body = await r.json().catch(() => ({}));
        if (!r.ok) throw new Error(body.detail || 'Could not load the invoice');
        setData(body);
      })
      .catch((e) => setError(e.message));
  }, [invoiceId]);

  const inv = data?.invoice;
  const st = inv ? (INVOICE_STATUS[inv.status] || INVOICE_STATUS.open) : null;
  const canPay = inv && (inv.status === 'open' || inv.status === 'past_due');

  const print = () => {
    const el = document.getElementById('billing-invoice-print');
    if (!el) return;
    const w = window.open('', '_blank', 'width=800,height=900');
    if (!w) return;
    w.document.write(`<!doctype html><html><head><title>Invoice ${inv.number}</title>
      <style>body{font-family:system-ui,sans-serif;color:#111;padding:32px;max-width:760px;margin:auto}
      table{width:100%;border-collapse:collapse}th,td{padding:8px 6px;border-bottom:1px solid #e5e7eb;text-align:left;font-size:13px;vertical-align:top}
      th{font-size:11px;text-transform:uppercase;color:#6b7280}.r{text-align:right}.muted{color:#6b7280;font-size:12px}
      .tot td{font-weight:700;border-bottom:none;font-size:15px}.grid{display:flex;justify-content:space-between;gap:24px;margin:16px 0}
      [data-noprint]{display:none}</style></head><body>${el.innerHTML}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };

  return (
    <Dialog open={!!invoiceId} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader>
          <DialogTitle>Invoice {inv?.number || ''}</DialogTitle>
          <DialogDescription>Itemized: your plan, then any texts or AI requests over the plan.</DialogDescription>
        </DialogHeader>
        {error && <p className="text-sm text-red-600">{error}</p>}
        {!inv && !error && <p className="text-sm text-gray-500 py-6 text-center">Loading...</p>}
        {inv && (
          <div id="billing-invoice-print" className="space-y-4 text-sm">
            <div className="flex flex-col sm:flex-row sm:justify-between gap-4">
              <div>
                <p className="text-xs uppercase text-gray-500">From</p>
                <p className="font-semibold">{data.from?.name}</p>
                <p className="muted text-gray-500">{data.from?.email}</p>
              </div>
              <div>
                <p className="text-xs uppercase text-gray-500">Billed to</p>
                <p className="font-semibold">{data.bill_to?.name}</p>
                {data.bill_to?.address && <p className="muted text-gray-500">{data.bill_to.address}</p>}
                {data.bill_to?.phone && <p className="muted text-gray-500">{data.bill_to.phone}</p>}
              </div>
              <div className="sm:text-right">
                <p className="text-xs uppercase text-gray-500">Invoice</p>
                <p className="font-mono font-semibold" data-testid="invoice-number">{inv.number}</p>
                <p className="muted text-gray-500">Issued {day(inv.created_at)}</p>
                <p className="muted text-gray-500">Due {day(inv.due_at)}</p>
                <Badge className={st.color} data-testid="invoice-status">{st.label}</Badge>
              </div>
            </div>
            <table className="w-full" data-testid="invoice-lines">
              <thead>
                <tr className="border-b">
                  <th className="text-left text-xs uppercase text-gray-500 py-2">Description</th>
                  <th className="r text-right text-xs uppercase text-gray-500 py-2">Qty</th>
                  <th className="r text-right text-xs uppercase text-gray-500 py-2">Unit</th>
                  <th className="r text-right text-xs uppercase text-gray-500 py-2">Amount</th>
                </tr>
              </thead>
              <tbody>
                {(inv.lines || []).map((ln) => (
                  <tr key={ln.id} className="border-b" data-testid={`invoice-line-${ln.kind}`}>
                    <td className="py-2 pr-2">{ln.description}</td>
                    <td className="r text-right py-2">{ln.quantity.toLocaleString()}</td>
                    <td className="r text-right py-2">{money(ln.unit_cents)}</td>
                    <td className="r text-right py-2">{money(ln.amount_cents)}</td>
                  </tr>
                ))}
                <tr className="tot">
                  <td className="py-2 font-semibold" colSpan={3}>Total</td>
                  <td className="r text-right py-2 font-bold" data-testid="invoice-total">{money(inv.amount_cents)}</td>
                </tr>
              </tbody>
            </table>
            <div className="muted text-gray-500 space-y-1">
              {inv.status === 'paid' && (
                <p data-testid="invoice-paid-note">
                  Paid {day(inv.paid_at)} with {inv.card_brand || 'card'} ending {inv.card_last4}
                  {inv.transaction_id ? ` (transaction ${inv.transaction_id})` : ''}.
                </p>
              )}
              {inv.status !== 'paid' && inv.failure_reason && (
                <p className="text-red-600" data-testid="invoice-failure">{inv.failure_reason}</p>
              )}
              {inv.status !== 'paid' && (
                <p>Autopay charges the card on file one day before the due date and retries once a day.</p>
              )}
            </div>
          </div>
        )}
        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" onClick={print} disabled={!inv} className="min-h-[44px]" data-testid="invoice-print">
            <Printer className="h-4 w-4 mr-2" />Print
          </Button>
          {canPay && (
            <Button onClick={() => onPay(inv.id)} disabled={paying} className="min-h-[44px]" data-testid="invoice-pay">
              {paying && <RefreshCw className="h-4 w-4 mr-2 animate-spin" />}Pay {money(inv.amount_cents)} now
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/** This period's texts and AI requests against the plan. */
export function UsageMeter({ usage }) {
  if (!usage) return null;
  const label = { sms: 'Text messages', ai: 'AI requests' };
  return (
    <div className="space-y-3" data-testid="usage-meter">
      {usage.items.map((it) => {
        const pct = it.included > 0 ? Math.min(100, Math.round((it.used / it.included) * 100)) : (it.used > 0 ? 100 : 0);
        return (
          <div key={it.kind} data-testid={`usage-${it.kind}`}>
            <div className="flex justify-between text-xs sm:text-sm">
              <span className="font-medium">{label[it.kind]}</span>
              <span className="text-gray-600">
                <span data-testid={`usage-${it.kind}-used`}>{it.used.toLocaleString()}</span> of {it.included.toLocaleString()} included
              </span>
            </div>
            <div className="h-2 rounded bg-gray-100 mt-1 overflow-hidden">
              <div className={`h-2 ${it.over > 0 ? 'bg-red-500' : 'bg-blue-500'}`} style={{ width: `${pct}%` }} />
            </div>
            {it.over > 0 && (
              <p className="text-xs text-red-600 mt-1">
                {it.over.toLocaleString()} over the plan at {money(it.unit_cents)} each
                {usage.billed ? ` = ${money(it.overage_cents)} on your next invoice` : ' (not billed during the free trial)'}
              </p>
            )}
          </div>
        );
      })}
      <p className="text-xs text-gray-500">
        Over the plan, each text is {money(usage.items.find((i) => i.kind === 'sms')?.unit_cents)} and each AI request{' '}
        {money(usage.items.find((i) => i.kind === 'ai')?.unit_cents)}, itemized on the next invoice.
      </p>
    </div>
  );
}
